import { ProductTypeQuestionState } from './product-type-question/product-type-question.component';
import { isBaxiManufacturer } from '../../../manage-my-repair/data/baxi-manufacturer-names';

export interface ManufacturerStepState {
  shouldAsk: boolean;
  options: string[];
  normalizedOptions: { title: string; normalized: string }[];
  searchOptions: { title: string; value: string }[];
  lastSearchTerm: string;
  selectedManufacturer: string | null;
}

export function createDefaultManufacturerState(): ManufacturerStepState {
  return {
    shouldAsk: false,
    options: [],
    normalizedOptions: [],
    searchOptions: [],
    lastSearchTerm: '',
    selectedManufacturer: null,
  };
}

export function createDefaultProductTypeState(
  shouldDisplayGasQuestion = false
): ProductTypeQuestionState {
  return {
    shouldAskProductTypeQuestion: false,
    selectedOption: null,
    shouldDisplayGasQuestion,
    gasSafetyAnswer: null,
    gasEmergencyReason: null,
    showGasEmergencyContent: false,
  };
}

export interface AddressQuestionViewModel {
  text: string;
  fontWeight: '400' | '600';
}

export interface NeedARepairViewModel {
  canShowClaimSelection: boolean;
  canShowInitialCta: boolean;
  canShowAddressConfirmation: boolean;
  canShowBookingCta: boolean;
  canShowProductTypeQuestion: boolean;
  canShowManufacturerQuestion: boolean;
  showGasEmergencyContent: boolean;
  addressQuestion: AddressQuestionViewModel;
  addressWarningText: string | null;
  applianceConditionQuestion;
  canShowApplianceCondition;
  canShowBaxiQuestion;
  canShowBaxiConfirmedNotice: boolean;
  canShowBaxiCallPanel: boolean;
  baxiLetterQuestion;
}

export interface NeedARepairViewModelInput {
  currentClaimTypes: any[];
  selectedOption: any;
  pastFirstStage: boolean;
  claimType: 'REPAIR' | 'SERVICE';
  manufacturerName: string | null;
  applianceInGoodCondition: boolean | undefined;
  receivedBaxiLetter: boolean | undefined;
  addressCorrect: boolean | undefined;
  productTypeState: ProductTypeQuestionState;
  hasProductTypeOptions: boolean;
  isGasPlan: boolean;
  manufacturerState: ManufacturerStepState;
}

export function createNeedARepairViewModel(
  input: NeedARepairViewModelInput
): NeedARepairViewModel {
  const {
    currentClaimTypes,
    selectedOption,
    pastFirstStage,
    addressCorrect,
    productTypeState,
    hasProductTypeOptions,
    isGasPlan,
    manufacturerState,
  } = input;

  const showGasEmergencyContent = productTypeState.showGasEmergencyContent;
  const hasSelectedProductType = productTypeState.selectedOption != null;

  // Gas safety step
  const requiresGasQuestion = productTypeState.shouldDisplayGasQuestion;
  const gasAnsweredNo = productTypeState.gasSafetyAnswer === 'NO';
  const gasAnsweredYes = productTypeState.gasSafetyAnswer === 'YES';
  const gasEmergencyResolved =
    productTypeState.gasSafetyAnswer === 'YES' &&
    !!productTypeState.gasEmergencyReason;
  const gasStepComplete =
    !requiresGasQuestion || gasAnsweredNo || gasEmergencyResolved;

  // Product type step
  const requiresProductTypeQuestion =
    hasProductTypeOptions && productTypeState.shouldAskProductTypeQuestion;
  const productTypeSelectionComplete =
    !hasProductTypeOptions ||
    !requiresProductTypeQuestion ||
    hasSelectedProductType;
  const productTypeStepComplete =
    productTypeSelectionComplete && gasStepComplete;

  // Manufacturer step
  const manufacturerSelectionComplete =
    !manufacturerState.shouldAsk || !!manufacturerState.selectedManufacturer;
  const manufacturerJustCompleted =
    manufacturerState.shouldAsk && manufacturerSelectionComplete;

  // All steps combined
  const allStepsComplete =
    productTypeStepComplete && manufacturerSelectionComplete;

  // Claim type helpers
  const hasSingleClaimType = currentClaimTypes.length === 1;
  const hasSelectedClaimType = !!selectedOption;
  const requiresClaimTypeSelection = currentClaimTypes.length > 1;

  // Visibility: claim selection tiles
  // Hide claim tiles when gas emergency is showing, unless multi-option.
  const canShowClaimSelection =
    !showGasEmergencyContent || requiresClaimTypeSelection;

  // Visibility: "Book a repair" CTA
  const canShowInitialCta =
    canShowClaimSelection &&
    hasSingleClaimType &&
    !pastFirstStage &&
    !showGasEmergencyContent;

  // Visibility: manufacturer question
  // Show after book a repair/service clicked for single claim types; for multi, after tile click.
  const canShowManufacturerQuestion =
    manufacturerState.shouldAsk &&
    (hasSelectedClaimType || hasSingleClaimType) &&
    !canShowInitialCta;

  // Visibility: product type question
  // Show after manufacturer is selected (if required).
  // For multi claim types, wait for tile selection (pastFirstStage).
  // For single claim types with manufacturer, show once manufacturer done.
  const hasProductTypeContent =
    hasProductTypeOptions ||
    requiresGasQuestion ||
    input.claimType === 'SERVICE';
  const requiresSelectionBeforeProductDisplay =
    hasProductTypeOptions && productTypeState.shouldAskProductTypeQuestion;
  const hasProductTypeEngagement =
    productTypeState.shouldDisplayGasQuestion ||
    productTypeState.gasSafetyAnswer !== null ||
    productTypeState.selectedOption !== null;
  const hasProductTypeTrigger =
    hasProductTypeContent &&
    (!requiresSelectionBeforeProductDisplay ||
      !!selectedOption ||
      manufacturerJustCompleted);
  const canShowProductTypeQuestion =
    hasProductTypeTrigger &&
    pastFirstStage &&
    (hasProductTypeEngagement ||
      manufacturerJustCompleted ||
      hasSingleClaimType ||
      (requiresClaimTypeSelection && hasSelectedClaimType)) &&
    manufacturerSelectionComplete &&
    !canShowInitialCta;

  // Visibility: address confirmation
  // Three paths reach the address step:
  // 1. Manufacturer flow - all steps done
  // 2. Product type flow - product type selected
  // 3. Standard flow - CTA clicked (pastFirstStage) + all steps done
  const manufacturerFlowAddressReady =
    manufacturerState.shouldAsk && hasSingleClaimType && allStepsComplete;
  const productTypeFlowAddressReady =
    !manufacturerState.shouldAsk &&
    hasProductTypeOptions &&
    hasSingleClaimType &&
    allStepsComplete;
  const standardFlowAddressReady =
    hasSelectedClaimType && pastFirstStage && allStepsComplete;

  const isService = input.claimType === 'SERVICE';
  const isBaxi = isBaxiManufacturer(input.manufacturerName);

  // For SERVICES only:
  // Show "Is your boiler in good working condition?":
  const applianceConditionReady =
    isService &&
    gasStepComplete &&
    productTypeStepComplete &&
    manufacturerSelectionComplete;

  // Show Baxi question:
  const baxiQuestionReady =
    isService &&
    isBaxi &&
    input.applianceInGoodCondition === true &&
    !gasAnsweredYes;

  // Address question visibility for services:
  const serviceAddressReady =
    isService &&
    ((isBaxi && input.receivedBaxiLetter === true) ||
      (!isBaxi && input.applianceInGoodCondition === true));

  const repairAddressReady =
    !isService &&
    (manufacturerFlowAddressReady ||
      productTypeFlowAddressReady ||
      standardFlowAddressReady) &&
    !canShowInitialCta;

  const canShowApplianceCondition =
    applianceConditionReady && !showGasEmergencyContent && !canShowInitialCta;

  const canShowBaxiQuestion =
    baxiQuestionReady && !showGasEmergencyContent && !canShowInitialCta;

  const canShowBaxiConfirmedNotice =
    canShowBaxiQuestion && input.receivedBaxiLetter === true;

  const canShowBaxiCallPanel =
    canShowBaxiQuestion && input.receivedBaxiLetter === false;

  const canShowAddressConfirmation =
    canShowClaimSelection &&
    !showGasEmergencyContent &&
    (serviceAddressReady || repairAddressReady) &&
    !canShowInitialCta;

  // Visibility: final booking CTA
  const canShowBookingCta =
    canShowAddressConfirmation && addressCorrect === true;

  const applianceConditionQuestion: AddressQuestionViewModel = {
    text: 'Is your boiler in good working condition?',
    fontWeight: '600',
  };

  const baxiLetterQuestion: AddressQuestionViewModel = {
    text: 'Have you received an email, letter, or text message from Baxi in the last 45 days inviting you to book your annual service?',
    fontWeight: '600',
  };

  const addressQuestion: AddressQuestionViewModel =
    requiresClaimTypeSelection || isGasPlan
      ? {
          text: 'Is this your address?',
          fontWeight: '600',
        }
      : {
          text: `Is this the right address for your ${
            isService ? 'service' : 'repair'
          }? You won't be able to change this later.`,
          fontWeight: '400',
        };

  const addressWarningText =
    requiresClaimTypeSelection || isGasPlan
      ? `We'll use this for your ${
          isService ? 'service' : 'repair'
        }. Check it's correct as you can't change it later.`
      : null;

  return {
    canShowClaimSelection,
    canShowInitialCta,
    canShowAddressConfirmation,
    canShowBookingCta,
    canShowProductTypeQuestion,
    canShowManufacturerQuestion,
    showGasEmergencyContent,
    addressQuestion,
    addressWarningText,
    applianceConditionQuestion,
    canShowApplianceCondition,
    canShowBaxiQuestion,
    canShowBaxiConfirmedNotice,
    canShowBaxiCallPanel,
    baxiLetterQuestion,
  };
}
